/**
 * ProjectSubmissionOversikt — v6.17 (DE)
 *
 * Admin-oversikt for prosjekt/portfolio-innleveringer per kandidat.
 * Vises i "Prøve/vurdering"-fanen når første komponent er project/portfolio.
 *
 * Features:
 * - Statuskort (totalt / ikke invitert / invitert / levert / sensurert)
 * - Deadline-nedtelling med effective_cutoff
 * - "Send invitasjonslenke til valgte"-knapp (bulk)
 * - Tabell per kandidat med fil-count, levert-tid, sensor-status
 * - Klikk på rad → åpne SensorProjectView
 */
import { useEffect, useState } from 'react';
import { authenticatedFetch } from '../lib/auth';
import {
  Mail, Clock, Calendar, FileCheck, FileWarning, AlertTriangle, Loader2,
  Send, CheckCircle, XCircle, Circle, Sparkles, FileText, ChevronRight
} from 'lucide-react';

interface SubmissionRow {
  submission_id: string;
  status: string;
  candidate_id: string;
  candidate_code: string | null;
  candidate_name: string | null;
  candidate_email: string | null;
  candidate_mobile: string | null;
  candidate_status: string;
  excluded: boolean;
  invitation_sent_at: string | null;
  last_invited_at: string | null;
  invitation_resent_count: number;
  invitation_email_sent: boolean;
  invitation_sms_sent: boolean;
  submitted_at: string | null;
  file_count: number;
  file_names: string[];
  total_size_bytes: number;
  sensor_score: number | null;
  sensor_grade: string | null;
  sensor_passed: boolean | null;
  sensor_graded_at: string | null;
  sensor_name: string | null;
  alternative_assessment_used: boolean;
}

interface ComponentData {
  component_id: string;
  component_title: string;
  component_type: 'project' | 'portfolio';
  deadline: string | null;
  grace_period_hours: number;
  is_anonymous: boolean;
  effective_cutoff: string | null;
  submissions: SubmissionRow[];
}

interface OverviewResponse {
  components: ComponentData[];
  totals: {
    total: number;
    not_started: number;
    invited: number;
    in_progress: number;
    submitted: number;
    graded: number;
    overridden_by_alternative: number;
  };
}

interface Props {
  courseInstanceId: string;
}

function formatOsloDateTime(iso: string | null): string {
  if (!iso) return 'ikke satt';
  try {
    return new Date(iso).toLocaleString('nb-NO', {
      timeZone: 'Europe/Oslo', dateStyle: 'long', timeStyle: 'short'
    });
  } catch {
    return iso;
  }
}

function formatRelativeTime(iso: string | null): { text: string; tone: 'ok' | 'warn' | 'bad' | 'done' } {
  if (!iso) return { text: 'ingen frist', tone: 'ok' };
  const now = Date.now();
  const target = new Date(iso).getTime();
  const diffMs = target - now;
  const absHours = Math.abs(diffMs / (1000 * 60 * 60));
  if (diffMs < 0) {
    return { text: `passert for ${absHours < 48 ? Math.round(absHours) + ' t' : Math.round(absHours / 24) + ' d'} siden`, tone: 'done' };
  }
  if (absHours < 24) return { text: `om ${Math.round(absHours)} timer`, tone: 'bad' };
  if (absHours < 48) return { text: `om ${Math.round(absHours)} timer`, tone: 'warn' };
  return { text: `om ${Math.round(absHours / 24)} dager`, tone: 'ok' };
}

function statusLabel(sub: SubmissionRow): { text: string; cls: string; icon: React.ReactNode } {
  if (sub.alternative_assessment_used) {
    return { text: 'Alternativ vurdering', cls: 'bg-purple-100 text-purple-800', icon: <Sparkles className="h-3.5 w-3.5" /> };
  }
  if (sub.status === 'graded') {
    const pass = sub.sensor_passed === true;
    return {
      text: `Sensurert · ${pass ? 'Bestått' : 'Ikke bestått'}`,
      cls: pass ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800',
      icon: pass ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />
    };
  }
  if (sub.status === 'ready_to_finalize' || sub.status === 'in_grading') {
    return { text: 'Hos sensor', cls: 'bg-kf-blue-100 text-kf-blue-800', icon: <Clock className="h-3.5 w-3.5" /> };
  }
  if (sub.status === 'submitted') {
    return { text: 'Levert — venter sensur', cls: 'bg-kf-blue-100 text-kf-blue-800', icon: <FileCheck className="h-3.5 w-3.5" /> };
  }
  if (sub.status === 'in_progress') {
    return { text: 'Underveis', cls: 'bg-amber-100 text-amber-800', icon: <FileWarning className="h-3.5 w-3.5" /> };
  }
  if (sub.status === 'not_started' && sub.invitation_sent_at) {
    return { text: 'Invitert — ikke startet', cls: 'bg-kf-blue-50 text-kf-blue-700', icon: <Mail className="h-3.5 w-3.5" /> };
  }
  return { text: 'Ikke invitert', cls: 'bg-gray-100 text-gray-700', icon: <Circle className="h-3.5 w-3.5" /> };
}

export default function ProjectSubmissionOversikt({ courseInstanceId }: Props) {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [resendOpen, setResendOpen] = useState(false);

  const loadOverview = () => {
    setLoading(true);
    setError(null);
    authenticatedFetch(`/api/admin/project-submissions/course/${courseInstanceId}/overview`)
      .then(r => {
        if (!r.ok) {
          if (r.status === 502 || r.status === 503 || r.status === 504) {
            const err: Error & { transient?: boolean } = new Error('Tjenesten er midlertidig utilgjengelig. Prøv igjen om et øyeblikk.');
            err.transient = true;
            throw err;
          }
          throw new Error(`HTTP ${r.status}`);
        }
        return r.json();
      })
      .then(json => { setData(json); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { loadOverview(); }, [courseInstanceId]);

  const toggleSelect = (subId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(subId)) next.delete(subId);
      else next.add(subId);
      return next;
    });
  };

  const invitableSubs = data?.components.flatMap(c =>
    c.submissions.filter(s =>
      !s.excluded &&
      s.candidate_status !== 'self_cancelled' &&
      s.candidate_status !== 'excluded' &&
      s.status !== 'graded' &&
      s.status !== 'overridden_by_alternative' &&
      !s.invitation_sent_at
    )
  ) ?? [];

  const selectAll = () => setSelected(new Set(invitableSubs.map(s => s.submission_id)));
  const clearSelection = () => setSelected(new Set());

  const sendInvitations = async (resendAll = false) => {
    if (!resendAll && selected.size === 0) {
      setSendResult('Velg minst én kandidat');
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const body: Record<string, unknown> = resendAll
        ? { resend_all: true }
        : { submission_ids: Array.from(selected) };
      const r = await authenticatedFetch(
        `/api/admin/project-submissions/course/${courseInstanceId}/send-invitations`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      const json = await r.json();
      if (!r.ok) {
        setSendResult(`Feil: ${json.error || 'ukjent'}`);
      } else {
        setSendResult(`${json.sent} sendt, ${json.failed} feilet.`);
        setSelected(new Set());
        setResendOpen(false);
        loadOverview();
      }
    } catch (e) {
      setSendResult(`Nettverksfeil: ${(e as Error).message}`);
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="p-8 text-center text-gray-600 flex items-center justify-center gap-2">
      <Loader2 className="h-5 w-5 animate-spin" /> Laster innleveringsoversikt…
    </div>
  );
  if (error) {
    const isTransient = /midlertidig utilgjengelig/.test(error);
    return (
      <div className={`${isTransient ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-red-50 border-red-200 text-red-800'} border rounded-lg p-4`}>
        <div className="flex items-center gap-2 font-medium mb-1">
          <AlertTriangle className="h-4 w-4" />
          {isTransient ? 'Prøv igjen om et øyeblikk' : 'Kunne ikke hente oversikt'}
        </div>
        <div className="text-sm">{error}</div>
        <button
          onClick={loadOverview}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-white border border-current rounded hover:bg-opacity-80"
        >
          Prøv igjen
        </button>
      </div>
    );
  }
  if (!data || data.components.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-amber-900">
        <div className="font-semibold mb-2">Ingen innleveringskomponent funnet for dette kurset</div>
        <p className="text-sm text-amber-800 mb-2">
          Denne fanen håndterer <strong>asynkrone digitale innleveringer</strong> — prosjektoppgaver
          og mappevurderinger (portfolio) der kandidaten laster opp filer og sensor vurderer senere.
        </p>
        <p className="text-sm text-amber-800 mb-2">
          Kurset ditt bruker en annen vurderingsform (f.eks. skriftlig prøve, muntlig,
          praktisk eller observasjon), eller vurderingsoppsettet er ikke konfigurert ennå.
        </p>
        <p className="text-sm text-amber-800">
          Åpne <strong>«Vurderingsoppsett og plan-innstillinger»</strong> øverst på denne fanen
          for å se eller endre vurderingsformen for kurset.
        </p>
      </div>
    );
  }

  const totalCandidates = Math.max(data.totals.total, 1);

  return (
    <div className="space-y-5">
      {/* Hjelpetekst */}
      <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 text-sm text-kf-blue-900">
        <p className="font-semibold mb-1">Slik fungerer innleveringsgjennomføringen</p>
        <ol className="list-decimal list-inside space-y-1 text-kf-blue-800">
          <li>Hak av kandidater som skal få invitasjonen (eller klikk «Alle»)</li>
          <li>Klikk <strong>Send innleveringsinvitasjon</strong> — e-post + SMS går ut med personlig lenke til innleveringssiden</li>
          <li>Kandidaten logger inn med <strong>Vipps</strong> og laster opp fil(er) før fristen</li>
          <li>Sensor sensurerer via <strong>Sensor-fanen</strong> når innlevering er markert «Levert»</li>
        </ol>
      </div>

      {/* Statuskort */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Totalt" value={data.totals.total} tone="gray" />
        <StatCard label="Ikke invitert" value={data.totals.not_started} tone="gray" />
        <StatCard label="Invitert" value={data.totals.invited + data.totals.in_progress} tone="blue" />
        <StatCard label="Levert" value={data.totals.submitted} tone="amber" />
        <StatCard label="Sensurert" value={data.totals.graded + data.totals.overridden_by_alternative} tone="green" />
      </div>

      {/* Per komponent */}
      {data.components.map(comp => {
        const rel = formatRelativeTime(comp.deadline);
        const relTone =
          rel.tone === 'bad' ? 'bg-red-50 border-red-200 text-red-900' :
          rel.tone === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-900' :
          rel.tone === 'done' ? 'bg-gray-50 border-gray-200 text-gray-700' :
          'bg-emerald-50 border-emerald-200 text-emerald-900';
        return (
          <div key={comp.component_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Komponent-header */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-kf-blue-600" />
                  {comp.component_title}
                  <span className="text-xs font-normal text-gray-500">
                    {comp.component_type === 'portfolio' ? 'Mappe' : 'Prosjekt'}
                  </span>
                  {comp.is_anonymous && (
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-800 rounded">Anonym</span>
                  )}
                </div>
              </div>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border ${relTone} text-sm`}>
                <Calendar className="h-4 w-4" />
                <span className="font-medium">Frist:</span>
                <span>{formatOsloDateTime(comp.deadline)}</span>
                <span className="opacity-75">({rel.text})</span>
              </div>
            </div>

            {/* Action-bar */}
            <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="font-medium">{selected.size}</span> av {invitableSubs.length} ikke-inviterte valgt
                {invitableSubs.length > 0 && (
                  <>
                    <button onClick={selectAll} className="ml-2 text-kf-blue-600 hover:underline text-xs">Alle</button>
                    <button onClick={clearSelection} className="text-kf-blue-600 hover:underline text-xs">Ingen</button>
                  </>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => setResendOpen(true)}
                  className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                  title="Send påminnelse til ALLE som er invitert men ikke har levert"
                >
                  Send påminnelse til alle
                </button>
                <button
                  onClick={() => sendInvitations(false)}
                  disabled={sending || selected.size === 0}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-kf-blue-600 hover:bg-kf-blue-700 disabled:bg-gray-300 text-white rounded font-medium text-sm"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send innleveringsinvitasjon ({selected.size})
                </button>
              </div>
            </div>

            {sendResult && (
              <div className="mx-4 my-2 text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2 rounded">
                {sendResult}
              </div>
            )}

            {/* Tabell */}
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="w-10 px-3 py-2"></th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Kandidat</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invitert</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Levert</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Filer</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sensor</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {comp.submissions.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-600 text-sm">
                        <div className="font-medium mb-1">Ingen kandidater er registrert ennå</div>
                        <div className="text-xs text-gray-500">
                          Så snart kandidater melder seg på kurset — manuelt via «Legg til
                          kandidat», CSV-import, eller selvpåmelding på kompetanseheving.no —
                          dukker de opp her og kan sendes innleveringsinvitasjon.
                        </div>
                      </td>
                    </tr>
                  )}
                  {comp.submissions.map(sub => {
                    const canInvite = !sub.excluded &&
                      sub.candidate_status !== 'self_cancelled' &&
                      sub.candidate_status !== 'excluded' &&
                      sub.status !== 'graded' &&
                      sub.status !== 'overridden_by_alternative' &&
                      !sub.invitation_sent_at;
                    const stLabel = statusLabel(sub);
                    return (
                      <tr key={sub.submission_id} className={sub.excluded ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-2 text-center">
                          {canInvite && (
                            <input
                              type="checkbox"
                              checked={selected.has(sub.submission_id)}
                              onChange={() => toggleSelect(sub.submission_id)}
                              className="h-4 w-4 text-kf-blue-600 rounded"
                            />
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <div className="font-medium text-gray-900">
                            {sub.candidate_code || (comp.is_anonymous ? '(anonym)' : sub.candidate_name || '(uten kode)')}
                          </div>
                          {!comp.is_anonymous && sub.candidate_name && (
                            <div className="text-xs text-gray-500">{sub.candidate_name}</div>
                          )}
                          {sub.excluded && (
                            <div className="text-xs text-red-600 italic">Ekskludert</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${stLabel.cls}`}>
                            {stLabel.icon}
                            {stLabel.text}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {sub.invitation_sent_at ? (
                            <div>
                              {formatOsloDateTime(sub.invitation_sent_at)}
                              {sub.invitation_resent_count > 0 && (
                                <div className="text-amber-600">+{sub.invitation_resent_count} påminnelse(r)</div>
                              )}
                              <div className="text-gray-400">
                                {sub.invitation_email_sent ? 'E-post ✓' : 'E-post —'}
                                {' · '}
                                {sub.invitation_sms_sent ? 'SMS ✓' : 'SMS —'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {sub.submitted_at ? formatOsloDateTime(sub.submitted_at) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {sub.file_count > 0 ? (
                            <div>
                              <span className="font-medium">{sub.file_count}</span>
                              <div className="text-gray-400">{(sub.total_size_bytes / (1024 * 1024)).toFixed(1)} MB</div>
                            </div>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {sub.sensor_graded_at ? (
                            <div>
                              <div className="text-gray-900">{sub.sensor_grade || (sub.sensor_passed ? 'Bestått' : 'Ikke bestått')}</div>
                              <div className="text-gray-400">{sub.sensor_name || 'ukjent sensor'}</div>
                            </div>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(sub.status === 'submitted' || sub.status === 'ready_to_finalize' ||
                            sub.status === 'in_grading' || sub.status === 'graded' ||
                            sub.status === 'overridden_by_alternative') && (
                            <a
                              href={`/sensor/project-submissions/${sub.submission_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-kf-blue-600 hover:underline"
                            >
                              Åpne <ChevronRight className="h-3 w-3" />
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Resend-all dialog */}
      {resendOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setResendOpen(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Send påminnelse til alle</h3>
            <p className="text-sm text-gray-700 mb-4">
              Dette sender e-post + SMS på nytt til ALLE kandidater som er invitert men ikke har finalisert innleveringen. invitation_resent_count økes med 1 per kandidat.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setResendOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-sm"
              >
                Avbryt
              </button>
              <button
                onClick={() => sendInvitations(true)}
                disabled={sending}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm font-medium"
              >
                {sending ? 'Sender…' : 'Send påminnelse til alle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progressbar for completion */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-900">Totalt sensurert</span>
          <span className="text-sm text-gray-600">
            {data.totals.graded + data.totals.overridden_by_alternative} av {data.totals.total}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-emerald-500 h-2.5 rounded-full"
            style={{ width: `${((data.totals.graded + data.totals.overridden_by_alternative) / totalCandidates) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone: 'gray' | 'blue' | 'amber' | 'green' }) {
  const colors = {
    gray: 'border-gray-200 text-gray-700',
    blue: 'border-kf-blue-200 text-kf-blue-700',
    amber: 'border-amber-200 text-amber-700',
    green: 'border-emerald-200 text-emerald-700'
  }[tone];
  return (
    <div className={`bg-white rounded-lg shadow-sm p-3 border ${colors}`}>
      <p className="text-xs">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}
